import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { getUserOrders, createOrder } from '../services/database';
import { Order, OrderStatus } from '../types/order';
import { CartItem } from '../types/cart';
import { Product } from '../types/product';
import Button from '../components/common/Button';
import './OrdersPage.css';

const OrdersPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [currentOrders, setCurrentOrders] = useState<Order[]>([]);
  const [previousOrders, setPreviousOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Pull to refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [startY, setStartY] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const loadOrders = useCallback(async () => {
    try {
      if (!currentUser) return;
      
      const orders = await getUserOrders(currentUser.uid);
      
      // Filter and sort current orders (pending, processing, shipped)
      const current = orders
        .filter(o => ['pending', 'processing', 'shipped'].includes(o.status))
        .sort((a, b) => b.orderDate.getTime() - a.orderDate.getTime());
      
      // Filter and sort previous orders (delivered, cancelled)
      const previous = orders
        .filter(o => ['delivered', 'cancelled'].includes(o.status))
        .sort((a, b) => b.orderDate.getTime() - a.orderDate.getTime());
      
      setCurrentOrders(current);
      setPreviousOrders(previous);
    } catch (error) {
      console.error('Failed to load orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      loadOrders();
    } else {
      setLoading(false);
    }
  }, [currentUser, loadOrders]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadOrders();
    setRefreshing(false);
  }, [loadOrders]);

  // Pull to refresh handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (window.scrollY === 0) {
      setStartY(e.touches[0].clientY);
      setIsPulling(true);
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isPulling || window.scrollY > 0) return;
    
    const currentY = e.touches[0].clientY;
    const distance = Math.max(0, currentY - startY);
    
    if (distance > 0) {
      e.preventDefault();
      setPullDistance(Math.min(distance * 0.5, 80));
    }
  }, [isPulling, startY]);

  const handleTouchEnd = useCallback(async () => {
    if (isPulling && pullDistance > 60) {
      await handleRefresh();
    }
    
    setIsPulling(false);
    setPullDistance(0);
    setStartY(0);
  }, [isPulling, pullDistance, handleRefresh]);

  // Create test orders for development
  const createTestOrders = async () => {
    if (!currentUser) return;
    
    try {
      const testProducts: Product[] = [
        {
          id: 'test-1',
          productId: 'gold_trophy',
          name: 'Gold Trophy',
          description: 'Beautiful gold trophy for winners',
          price: 45.99,
          priceFormatted: '€45.99',
          size: 'Large',
          category: 'Trophies',
          images: ['/assets/products/gold_trophy.png'],
          stockQuantity: 10,
          sku: 'GT-001',
          page: 1,
          inStock: true,
        },
        {
          id: 'test-2',
          productId: 'silver_medal',
          name: 'Silver Medal',
          description: 'High quality silver medal',
          price: 12.50,
          priceFormatted: '€12.50',
          size: 'Standard',
          category: 'Medals',
          images: ['/assets/products/silver_medal.png'],
          stockQuantity: 25,
          sku: 'SM-001',
          page: 1,
          inStock: true,
        },
      ];

      const testItems: CartItem[] = [
        {
          product: testProducts[0],
          quantity: 2,
        },
        {
          product: testProducts[1],
          quantity: 5,
        },
      ];

      const testOrders: Omit<Order, 'id'>[] = [
        {
          userId: currentUser.uid,
          items: testItems,
          totalAmount: 154.48,
          status: OrderStatus.PENDING,
          orderDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
          shippingAddress: '123 Test Street, Test City, Ireland',
          customerName: 'Test User',
          customerEmail: currentUser.email || 'test@example.com',
          customerPhone: '+353 1 234 5678',
          paymentIntentId: 'pi_test_pending',
        },
        {
          userId: currentUser.uid,
          items: [{ product: testProducts[0], quantity: 2 }],
          totalAmount: 91.98,
          status: OrderStatus.PROCESSING,
          orderDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
          shippingAddress: '123 Test Street, Test City, Ireland',
          customerName: 'Test User',
          customerEmail: currentUser.email || 'test@example.com',
          customerPhone: '+353 1 234 5678',
          paymentIntentId: 'pi_test_processing',
        },
        {
          userId: currentUser.uid,
          items: [{ product: testProducts[1], quantity: 5 }],
          totalAmount: 62.50,
          status: OrderStatus.SHIPPED,
          orderDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), // 7 days ago
          shippingAddress: '123 Test Street, Test City, Ireland',
          customerName: 'Test User',
          customerEmail: currentUser.email || 'test@example.com',
          customerPhone: '+353 1 234 5678',
          paymentIntentId: 'pi_test_shipped',
        },
        {
          userId: currentUser.uid,
          items: testItems,
          totalAmount: 154.48,
          status: OrderStatus.DELIVERED,
          orderDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000), // 14 days ago
          shippingAddress: '123 Test Street, Test City, Ireland',
          customerName: 'Test User',
          customerEmail: currentUser.email || 'test@example.com',
          customerPhone: '+353 1 234 5678',
          paymentIntentId: 'pi_test_delivered',
        },
        {
          userId: currentUser.uid,
          items: [{ product: testProducts[0], quantity: 2 }],
          totalAmount: 91.98,
          status: OrderStatus.CANCELLED,
          orderDate: new Date(Date.now() - 21 * 24 * 60 * 60 * 1000), // 21 days ago
          shippingAddress: '123 Test Street, Test City, Ireland',
          customerName: 'Test User',
          customerEmail: currentUser.email || 'test@example.com',
          customerPhone: '+353 1 234 5678',
          paymentIntentId: 'pi_test_cancelled',
        },
      ];

      for (const orderData of testOrders) {
        const orderId = `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const order: Order = { ...orderData, id: orderId };
        await createOrder(currentUser.uid, order);
      }

      toast.success('Test orders created successfully!');
      await loadOrders();
    } catch (error) {
      console.error('Failed to create test orders:', error);
      toast.error('Failed to create test orders');
    }
  };

  const getStatusBadgeStyle = (status: OrderStatus) => {
    switch (status) {
      case OrderStatus.PENDING:
        return {
          background: '#FEF3C7',
          color: '#F59E0B',
          text: 'PENDING',
        };
      case OrderStatus.PROCESSING:
        return {
          background: '#DBEAFE',
          color: '#3B82F6',
          text: 'PROCESSING',
        };
      case OrderStatus.SHIPPED:
        return {
          background: '#FEF3C7',
          color: '#B8860B',
          text: 'SHIPPED',
        };
      case OrderStatus.DELIVERED:
        return {
          background: '#D1FAE5',
          color: '#10B981',
          text: 'DELIVERED',
        };
      case OrderStatus.CANCELLED:
        return {
          background: '#FEE2E2',
          color: '#EF4444',
          text: 'CANCELLED',
        };
      default:
        return {
          background: '#F3F4F6',
          color: '#6B7280',
          text: 'UNKNOWN',
        };
    }
  };

  const OrderCard: React.FC<{ order: Order }> = ({ order }) => {
    const statusStyle = getStatusBadgeStyle(order.status);
    const itemCount = order.items.reduce((sum, item) => sum + item.quantity, 0);

    return (
      <div className="order-card">
        {/* Header Row */}
        <div className="order-card-header">
          <div className="order-id">#{order.id.substring(0, 8)}</div>
          <div 
            className="status-badge"
            style={{
              backgroundColor: statusStyle.background,
              color: statusStyle.color,
            }}
          >
            {statusStyle.text}
          </div>
        </div>

        {/* Item Count */}
        <div className="order-item-count">
          {itemCount} item{itemCount !== 1 ? 's' : ''}
        </div>

        {/* Total Amount */}
        <div className="order-total">
          €{order.totalAmount.toFixed(2)}
        </div>

        {/* Order Date */}
        <div className="order-date">
          Ordered on {format(order.orderDate, 'MMM dd, yyyy')}
        </div>

        {/* Progress Bar for Shipped Orders */}
        {order.status === OrderStatus.SHIPPED && (
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress-fill"></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Loading State
  if (loading) {
    return (
      <div className="orders-page">
        <div className="orders-header">
          <h1 className="orders-title">Orders</h1>
        </div>
        <div className="orders-loading">
          {/* Skeleton loaders */}
          {[1, 2, 3].map((i) => (
            <div key={i} className="order-card-skeleton">
              <div className="skeleton-header">
                <div className="skeleton-text skeleton-id"></div>
                <div className="skeleton-text skeleton-badge"></div>
              </div>
              <div className="skeleton-text skeleton-count"></div>
              <div className="skeleton-text skeleton-total"></div>
              <div className="skeleton-text skeleton-date"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Not authenticated - show guest view
  if (!currentUser) {
    return (
      <div className="orders-page">
        <div className="orders-header">
          <h1 className="orders-title">Orders</h1>
        </div>
        <div className="guest-prompt">
          <div className="guest-prompt-icon">📦</div>
          <h2 className="guest-prompt-title">Track Your Orders</h2>
          <p className="guest-prompt-text">
            Sign in to view your order history and track current orders.
          </p>
          <div className="guest-prompt-buttons">
            <Button 
              onClick={() => navigate('/login')} 
              style={{
                backgroundColor: '#B8860B',
                color: 'white',
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '30px',
                border: 'none',
                width: '100%',
                marginBottom: '12px'
              }}
            >
              Log In
            </Button>
            <Button 
              onClick={() => navigate('/register')} 
              variant="outline"
              style={{
                backgroundColor: 'transparent',
                color: '#B8860B',
                padding: '14px 32px',
                fontSize: '16px',
                fontWeight: 600,
                borderRadius: '30px',
                border: '2px solid #B8860B',
                width: '100%'
              }}
            >
              Create Account
            </Button>
          </div>
          <p className="guest-prompt-note">
            You can browse and add items to cart without an account.
            <br />
            Sign in is only required at checkout.
          </p>
        </div>
      </div>
    );
  }

  const activeOrdersCount = currentOrders.length;

  return (
    <div 
      className="orders-page" 
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{
        transform: `translateY(${pullDistance}px)`,
        transition: isPulling ? 'none' : 'transform 0.3s ease',
      }}
    >
      {/* Pull to Refresh Indicator */}
      {pullDistance > 0 && (
        <div 
          className="pull-refresh-indicator"
          style={{
            opacity: Math.min(pullDistance / 60, 1),
            transform: `translateY(${pullDistance - 80}px)`,
          }}
        >
          <div className={`refresh-spinner ${pullDistance > 60 ? 'active' : ''}`}>
            {refreshing ? '🔄' : '↓'}
          </div>
          <span className="refresh-text">
            {refreshing ? 'Refreshing...' : pullDistance > 60 ? 'Release to refresh' : 'Pull to refresh'}
          </span>
        </div>
      )}

      {/* Header Section */}
      <div className="orders-header">
        <h1 className="orders-title">Orders</h1>
        
        {/* Active Orders Banner */}
        {activeOrdersCount > 0 && (
          <div className="active-orders-banner">
            <span className="shipping-icon">🚚</span>
            <span className="banner-text">
              You have {activeOrdersCount} order{activeOrdersCount !== 1 ? 's' : ''} on the way
            </span>
          </div>
        )}
      </div>

      {/* Refresh Button for Testing */}
      <div className="refresh-container">
        <button 
          className="refresh-button"
          onClick={handleRefresh}
          disabled={refreshing}
        >
          {refreshing ? '🔄' : '↻'} Refresh
        </button>
        
        {/* Test Data Button - Remove in production */}
        {process.env.NODE_ENV === 'development' && (
          <button 
            className="refresh-button"
            onClick={createTestOrders}
            style={{ marginLeft: '8px', backgroundColor: '#B8860B', color: 'white' }}
          >
            Add Test Orders
          </button>
        )}
      </div>

      {/* Orders Content */}
      <div className="orders-content">
        {/* Current Orders Section */}
        {currentOrders.length > 0 && (
          <div className="orders-section">
            <div className="section-header">
              <h2 className="section-title">Current Orders</h2>
            </div>
            <div className="orders-list">
              {currentOrders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))}
            </div>
          </div>
        )}

        {/* Previous Orders Section */}
        <div className="orders-section previous-orders-section">
          <div className="section-header">
            <h2 className="section-title">Previous Orders</h2>
          </div>
          <div className="orders-list">
            {previousOrders.length > 0 ? (
              previousOrders.map((order) => (
                <OrderCard key={order.id} order={order} />
              ))
            ) : (
              <div className="empty-state">
                <div className="empty-icon">📄</div>
                <p className="empty-text">No previous orders</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrdersPage;
